import type { CSSProperties } from 'react';

/**
 * Per-image layout inside a gallery block.
 *
 * `props.imageLayouts` is index-aligned with `props.images`: each entry may set
 * how many columns/rows that one image spans (bento-style grids) and its own
 * object-fit. Everything is optional — a gallery without the array renders
 * exactly as before, so existing blocks are untouched.
 */
export interface GalleryImageLayout {
  /** Columns this image spans in grid/masonry; widens the item in a carousel. */
  colSpan?: number;
  /** Rows this image spans (grid/masonry only). */
  rowSpan?: number;
  /** object-fit override for this one image. */
  fit?: string;
}

export const galleryLayoutsOf = (p: any): GalleryImageLayout[] =>
  Array.isArray(p?.imageLayouts) ? p.imageLayouts : [];

export const gallerySpans = (p: any, count: number): number[] =>
  Array.from({ length: count }, (_, i) => Math.max(1, Math.round(galleryLayoutsOf(p)[i]?.colSpan || 1)));

export const galleryFits = (p: any, count: number): (string | undefined)[] =>
  Array.from({ length: count }, (_, i) => galleryLayoutsOf(p)[i]?.fit || undefined);

/** True when any image asks for more than one cell — the grid then needs
 *  explicit equal-height rows, which is not the legacy behaviour. */
export const galleryHasSpans = (p: any): boolean =>
  galleryLayoutsOf(p).some(l => (l?.colSpan && l.colSpan > 1) || (l?.rowSpan && l.rowSpan > 1));

/** Grid placement for one image. Empty object ⇒ legacy single-cell rendering. */
export function galleryCellStyle(p: any, i: number, cols: number): CSSProperties {
  const l = galleryLayoutsOf(p)[i];
  if (!l) return {};
  const col = Math.max(1, Math.min(cols, Math.round(l.colSpan || 1)));
  const row = Math.max(1, Math.round(l.rowSpan || 1));
  return {
    ...(col > 1 ? { gridColumn: `span ${col}` } : {}),
    ...(row > 1 ? { gridRow: `span ${row}` } : {}),
  };
}

/** object-fit for one image, falling back to the gallery-wide setting. */
export const galleryFitFor = (p: any, i: number, fallback: string): string =>
  galleryLayoutsOf(p)[i]?.fit || fallback;

/** Arrow styling for the carousel, read off the block props with defaults.
 *  One place, so the canvas / preview / public page can't disagree. */
export function galleryArrowProps(p: any) {
  return {
    arrowColor: p?.arrowColor || '#111827',
    arrowBg: p?.arrowBg || 'rgba(255,255,255,0.92)',
    arrowSize: typeof p?.arrowSize === 'number' ? p.arrowSize : 34,
    arrowRadius: typeof p?.arrowRadius === 'number' ? p.arrowRadius : 50,
    arrowShadow: p?.arrowShadow !== false,
    arrowIcon: (p?.arrowIcon || 'chevron') as 'chevron' | 'arrow' | 'triangle',
  };
}

/**
 * Apply the same reorder/removal to `imageLayouts` that was applied to
 * `images`, so a per-image setting never drifts onto a different picture.
 * `order` lists the OLD indexes in their new order (missing = removed).
 */
export function reorderGalleryLayouts(p: any, order: number[]): GalleryImageLayout[] | undefined {
  const layouts = galleryLayoutsOf(p);
  if (!layouts.length) return undefined;
  return order.map(oldIdx => layouts[oldIdx] || {});
}
